<script setup lang="ts">
  import type { CalendarDate } from '@internationalized/date';
  import dayjs from 'dayjs';
  import { ref, watch } from 'vue';

  import { Button } from '@ui/components/button';
  import { Calendar } from '@ui/components/calendar';
  import { Popover, PopoverContent, PopoverTrigger } from '@ui/components/popover';
  import { Timestamp } from '@ui/components/time';
  import { cn } from '@ui/utils/utils';

  const props = defineProps<{
    modelValue?: CalendarDate | null;
  }>();

  const emits = defineEmits<{
    (e: 'update:modelValue', date: CalendarDate | null): void;
  }>();

  const dateSelectorOpen = ref<boolean>(false);
  const selectedDate = ref(props.modelValue ?? null);

  // Watch for external changes to modelValue
  watch(
    () => props.modelValue,
    (newValue) => {
      selectedDate.value = newValue ?? null;
    },
  );
</script>

<template>
  <div v-bind="$attrs" :class="cn('h-8 rounded-sm flex items-center truncate border')">
    <template v-if="selectedDate">
      <div class="flex text-xs gap-2 px-3 items-center font-medium h-full border-l-md">
        <i class="fa fa-calendar" />
        Date
      </div>
    </template>

    <Popover v-model:open="dateSelectorOpen">
      <PopoverTrigger as-child>
        <Button variant="ghost" size="sm" :class="cn(selectedDate && 'rounded-none border-l')">
          <template v-if="!selectedDate">
            <i class="fa fa-calendar" />
            Date
          </template>

          <template v-else>
            <Timestamp v-if="selectedDate" :time="dayjs.utc(selectedDate.toString())" date-only />
          </template>
        </Button>
      </PopoverTrigger>

      <PopoverContent class="w-auto p-3 space-y-3">
        <!-- @vue-ignore -->
        <Calendar
          v-model="selectedDate"
          @update:model-value="
            (d) => {
              dateSelectorOpen = false;
              emits('update:modelValue', d);
            }
          "
        />
      </PopoverContent>
    </Popover>

    <template v-if="selectedDate">
      <Button
        variant="ghost"
        size="sm"
        class="rounded-l-none border-l px-2"
        @click="
          () => {
            selectedDate = null;
            emits('update:modelValue', null);
          }
        "
      >
        <i class="far fa-xmark text-2xs" />
      </Button>
    </template>
  </div>
</template>
